import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  InputAdornment,
  CircularProgress,
  Paper,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  History as HistoryIcon,
  FilterList as FilterIcon,
  Person as PersonIcon,
  Event as EventIcon,
  Computer as ComputerIcon,
} from '@mui/icons-material';
import { getActivityLogs, downloadActivityLogPdf } from '../../services/activityLog';
import ExportButton from '../../components/ExportButton/ExportButton';
import { exportAsPDF, exportAsCSV, exportAsExcel } from '../../utils/importExport';

const ActivityLog = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [filters, setFilters] = useState({
    action: 'All',
    module: 'All',
    actorType: 'All',
    search: '',
    fromDate: '',
    toDate: '',
  });

  const actionOptions = ['All', 'create', 'update', 'delete', 'login', 'logout'];
  const moduleOptions = [
    'All',
    'login',
    'roleMaster',
    'changeAssociate',
    'associate',
    'customer',
    'lead',
    'booking',
    'project',
  ];
  const actorTypeOptions = ['All', 'admin', 'associate', 'customer', 'system', 'other'];

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };

      if (filters.action !== 'All') params.action = filters.action;
      if (filters.module !== 'All') params.module = filters.module;
      if (filters.actorType !== 'All') params.actorType = filters.actorType;
      if (filters.search) params.search = filters.search;
      if (filters.fromDate) params.fromDate = filters.fromDate;
      if (filters.toDate) params.toDate = filters.toDate;

      const response = await getActivityLogs(params);
      if (response.status && response.data) {
        setLogs(response.data.docs || []);
        setTotalDocs(response.data.totalDocs || 0);
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
    setPage(0);
  };

  const handleReset = () => {
    setFilters({
      action: 'All',
      module: 'All',
      actorType: 'All',
      search: '',
      fromDate: '',
      toDate: '',
    });
    setPage(0);
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'create':
        return 'success';
      case 'update':
        return 'info';
      case 'delete':
        return 'error';
      case 'login':
        return 'primary';
      default:
        return 'default';
    }
  };

  const getExportFileName = () => `Activity_Logs_${new Date().toISOString().split('T')[0]}`;

  const buildExportRows = () => {
    return logs.map((log, index) => ({
      'S.No': page * rowsPerPage + index + 1,
      'Date & Time': new Date(log.createdAt).toLocaleString('en-IN'),
      Actor: log.actorName || log.actorType || 'System',
      Action: log.action,
      Module: log.module,
      Message: log.message,
      'IP Address': log.ipAddress,
    }));
  };

  const handleExportCsv = () => {
    const rows = buildExportRows();
    exportAsCSV({ rows, fileNameBase: getExportFileName() });
  };

  const handleExportPdf = async () => {
    setLoading(true); // Using existing loading state, or could add a specific exportLoading state
    try {
      const params = {};
      if (filters.action !== 'All') params.action = filters.action;
      if (filters.module !== 'All') params.module = filters.module;
      if (filters.actorType !== 'All') params.actorType = filters.actorType;
      if (filters.search) params.search = filters.search;
      if (filters.fromDate) params.fromDate = filters.fromDate;
      if (filters.toDate) params.toDate = filters.toDate;

      const blob = await downloadActivityLogPdf(params);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${getExportFileName()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      // Optional: Show alert/toast here
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = () => {
    const rows = buildExportRows();
    exportAsExcel({
      rows,
      sheetName: 'Logs',
      fileNameBase: getExportFileName(),
    });
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', mb: 1, display: 'flex', alignItems: 'center' }}>
            <HistoryIcon sx={{ mr: 1.5, fontSize: 35, color: '#6366f1' }} />
            Activity Logs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor and track all system activities and user actions
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchLogs}
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              borderColor: '#e2e8f0',
              color: '#475569',
              '&:hover': { backgroundColor: '#f1f5f9' },
            }}
          >
            Refresh
          </Button>
          <ExportButton
            onExportPdf={handleExportPdf}
            onExportCsv={handleExportCsv}
            onExportExcel={handleExportExcel}
          />
        </Box>
      </Box>

      {/* Filters Card */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
            <FilterIcon sx={{ mr: 1, color: '#64748b' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#334155' }}>Filters</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2.5}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                placeholder="Message, actor name..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: '#94a3b8' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} md={1.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Action</InputLabel>
                <Select
                  value={filters.action}
                  label="Action"
                  onChange={(e) => handleFilterChange('action', e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  {actionOptions.map((opt) => (
                    <MenuItem key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={1.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Module</InputLabel>
                <Select
                  value={filters.module}
                  label="Module"
                  onChange={(e) => handleFilterChange('module', e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  {moduleOptions.map((opt) => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={1.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Actor Type</InputLabel>
                <Select
                  value={filters.actorType}
                  label="Actor Type"
                  onChange={(e) => handleFilterChange('actorType', e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  {actorTypeOptions.map((opt) => (
                    <MenuItem key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={1.75}>
              <TextField
                fullWidth
                size="small"
                label="From Date"
                type="date"
                value={filters.fromDate}
                onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} md={1.75}>
              <TextField
                fullWidth
                size="small"
                label="To Date"
                type="date"
                value={filters.toDate}
                onChange={(e) => handleFilterChange('toDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} md={1}>
              <Button
                fullWidth
                variant="text"
                onClick={handleReset}
                sx={{ height: '40px', textTransform: 'none', fontWeight: 600, color: '#6366f1' }}
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table Section */}
      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)', overflow: 'hidden' }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead sx={{ backgroundColor: '#f1f5f9' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, color: '#475569' }}>S.No</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#475569' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}><EventIcon sx={{ mr: 0.5, fontSize: 18 }} /> Date & Time</Box>
              </TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#475569' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}><PersonIcon sx={{ mr: 0.5, fontSize: 18 }} /> Actor</Box>
              </TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Action</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Module</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Message</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#475569' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}><ComputerIcon sx={{ mr: 0.5, fontSize: 18 }} /> IP Address</Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 10 }}>
                  <CircularProgress size={40} sx={{ color: '#6366f1' }} />
                  <Typography sx={{ mt: 2, color: '#64748b' }}>Fetching logs...</Typography>
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 10 }}>
                  <Typography variant="h6" color="text.secondary">No activity logs found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log, index) => (
                <TableRow key={log._id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155' }}>
                      {new Date(log.createdAt).toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(log.createdAt).toLocaleTimeString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                      {log.actorName || 'System'}
                    </Typography>
                    <Typography variant="caption" sx={{ textTransform: 'uppercase', color: '#64748b' }}>
                      {log.actorType}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={log.action}
                      size="small"
                      color={getActionColor(log.action)}
                      sx={{ fontWeight: 600, textTransform: 'capitalize', borderRadius: 1.5 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: '#475569' }}>{log.module}</Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 300 }}>
                    <Typography variant="body2" sx={{ color: '#334155' }}>{log.message}</Typography>
                    {log.entityType && (
                      <Typography variant="caption" color="text.secondary">
                        Action By: {log.actorName}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#64748b' }}>{log.ipAddress}</Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalDocs}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ borderTop: '1px solid #e2e8f0' }}
        />
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Typography variant="caption" color="text.secondary">
            Showing results for: {Object.entries(filters).filter(([k, v]) => v && v !== 'All').map(([k, v]) => `${k}=${v}`).join(', ') || 'All'}
          </Typography>
        </Box>
      </TableContainer>
    </Box>
  );
};

export default ActivityLog;
